import apiClient from './api';
import type {
  CreateTestimonialDTO,
  TestimonialResponse,
  TestimonialsPageResponse,
} from '../types';

// -----------------------------------------------------------------
// Social identity fetch helpers (client-side OAuth popups)
// -----------------------------------------------------------------

export interface SocialProfile {
  providerName: string;
  providerUserId: string;
  displayName: string;
  profilePictureUrl: string | null;
  email: string | null;
}

function openAuthPopup(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const width = 600;
    const height = 700;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const left = window.screenX + (window.outerWidth - width) / 2;

    const popup = window.open(
      url,
      'social-auth',
      `width=${width},height=${height},top=${top},left=${left},scrollbars=yes`
    );

    if (!popup) {
      reject(new Error('Popup blocked — please allow popups and try again.'));
      return;
    }

    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'AUTH_SUCCESS') {
        window.removeEventListener('message', handler);
        popup.close();
        resolve(event.data.token);
      } else if (event.data?.type === 'AUTH_ERROR') {
        window.removeEventListener('message', handler);
        popup.close();
        reject(new Error(event.data.error || 'Authentication was cancelled.'));
      }
    };

    window.addEventListener('message', handler);

    const interval = setInterval(() => {
      if (popup.closed) {
        clearInterval(interval);
        window.removeEventListener('message', handler);
        reject(new Error('Authentication was closed.'));
      }
    }, 1000);
  });
}

// Facebook: uses the Facebook JS SDK or manual OAuth popup flow
export async function verifyWithFacebook(): Promise<SocialProfile> {
  const APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID || '';

  if (!APP_ID) {
    // Demo mode: return a mock verified profile so the flow works in preview
    return {
      providerName: 'facebook',
      providerUserId: `demo-fb-${Date.now()}`,
      displayName: 'Demo User (Facebook)',
      profilePictureUrl: null,
      email: 'demo@example.com',
    };
  }

  const redirectUri = `${window.location.origin}/auth/callback`;
  const authUrl = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${APP_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=public_profile,email&response_type=token`;

  // Fallback: open a popup directing user to our /auth/callback flow
  const token = await openAuthPopup(authUrl);

  // Decode the token to extract user info (in production, verify server-side)
  // For demo, we parse the short-lived token info from Graph API
  const response = await fetch(
    `https://graph.facebook.com/me?fields=id,name,picture&access_token=${token}`
  );
  const data = await response.json();
  const picUrl = data.picture?.data?.url ?? null;

  return {
    providerName: 'facebook',
    providerUserId: data.id,
    displayName: data.name || 'Facebook User',
    profilePictureUrl: picUrl,
    email: data.email ?? null,
  };
}

// Google: uses Google OAuth 2.0 Implicit Flow
export async function verifyWithGoogle(): Promise<SocialProfile> {
  const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

  if (!CLIENT_ID) {
    // Demo mode: return a mock verified profile so the flow works in preview
    return {
      providerName: 'google',
      providerUserId: `demo-g-${Date.now()}`,
      displayName: 'Demo User (Google)',
      profilePictureUrl: null,
      email: 'demo@example.com',
    };
  }

  const redirectUri = `${window.location.origin}/auth/callback`;
  const authUrl =
    'https://accounts.google.com/o/oauth2/v2/auth?' +
    `client_id=${CLIENT_ID}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    '&response_type=token' +
    '&scope=profile%20email';

  const token = await openAuthPopup(authUrl);

  // Use the token to fetch profile
  const response = await fetch(
    'https://www.googleapis.com/oauth2/v3/userinfo',
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  const data = await response.json();

  return {
    providerName: 'google',
    providerUserId: data.sub,
    displayName: data.name || data.given_name || 'Google User',
    profilePictureUrl: data.picture ?? null,
    email: data.email ?? null,
  };
}

// -----------------------------------------------------------------
// API service methods
// -----------------------------------------------------------------

export const testimonialService = {
  async create(data: CreateTestimonialDTO): Promise<TestimonialResponse> {
    const response = await apiClient.post<TestimonialResponse>(
      '/testimonials',
      data
    );
    return response.data;
  },

  async getAll(page = 1, pageSize = 20): Promise<TestimonialsPageResponse> {
    const response = await apiClient.get<TestimonialsPageResponse>(
      `/testimonials?page=${page}&pageSize=${pageSize}`
    );
    return response.data;
  },
};

export { apiClient };
